import { useState } from 'react';
import { ShieldOff } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { couperAgregateur } from '@/shared/api/bridge';
import { nomConfirme } from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';

/**
 * Le bouton rouge : couper TOUS les accès d'un agrégateur, d'un geste, depuis l'écran.
 *
 * POURQUOI À CÔTÉ DE LA RÉVOCATION UNITAIRE
 *
 * Une clé qui a fui, un partenaire qui sort du contrat, une nuit où il faut agir vite :
 * se demander laquelle des trois clés était en cause fait perdre les minutes qui
 * comptent. La révocation unitaire reste, pour l'entretien courant.
 *
 * Même confirmation que la révocation d'une clé (taper le nom de l'agrégateur), dans la
 * page et jamais par `window.confirm`. Les actifs déclarés sont CONSERVÉS : rendre
 * l'accès plus tard se fait en créant une clé, sans reconstruire le périmètre.
 */
export default function CoupureTotale({ agregateur, label, actives, surCoupure }) {
  const [ouvert, setOuvert] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [bilan, setBilan] = useState(null);

  const couper = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const rendu = await couperAgregateur(agregateur);
      setBilan(rendu.revoquees);
      setOuvert(false);
      setSaisie('');
      await surCoupure?.();
    } catch (exc) {
      setErreur(exc);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border
                    border-red-200 bg-red-50/50 px-3 py-2" data-testid="bridge-coupure">
      <p className="text-[12.5px] text-neutral-700">
        {actives > 0
          ? `${label} a ${actives} clé${actives > 1 ? 's' : ''} active${actives > 1 ? 's' : ''}.`
          : `${label} n'a aucune clé active.`}
        {bilan !== null && (
          <span className="ml-2 font-medium text-red-700" data-testid="bridge-coupure-bilan">
            {bilan} clé{bilan > 1 ? 's' : ''} révoquée{bilan > 1 ? 's' : ''}.
          </span>
        )}
      </p>
      <Button size="sm" variant="destructive" disabled={actives === 0}
              onClick={() => { setOuvert(true); setSaisie(''); setBilan(null); }}
              data-testid="bridge-coupure-ouvrir">
        <ShieldOff className="mr-1.5 h-4 w-4" aria-hidden /> Couper tous les accès de {label}
      </Button>

      <ErreurBloc erreur={erreur} testid="bridge-coupure-erreur" />

      <Dialog open={ouvert} onOpenChange={(o) => !o && !enCours && setOuvert(false)}>
        <DialogContent className="max-w-md" data-testid="bridge-coupure-dialogue">
          <DialogHeader>
            <DialogTitle className="text-base">Couper tous les accès de {label} ?</DialogTitle>
            <DialogDescription className="text-[13px] leading-relaxed">
              {actives > 1 ? `Les ${actives} clés de ${label} cessent` : `La clé de ${label} cesse`} de
              fonctionner à la requête suivante. Les autres agrégateurs ne sont pas touchés, et
              les actifs déclarés restent en place : pour rendre l&apos;accès, il suffira de
              créer une nouvelle clé.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1 text-[12.5px] text-neutral-600">
            Tapez « {label} » pour confirmer
            <Input value={saisie} onChange={(e) => setSaisie(e.target.value)} autoFocus
                   data-testid="bridge-coupure-saisie" />
          </label>
          <DialogFooter>
            <Button size="sm" variant="outline" disabled={enCours}
                    onClick={() => setOuvert(false)}>Annuler</Button>
            <Button size="sm" variant="destructive"
                    disabled={!nomConfirme(saisie, label) || enCours}
                    onClick={couper} data-testid="bridge-coupure-confirmer">
              Couper tous les accès
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
